import { Component } from '@theme/component';
import { prefersReducedMotion } from '@theme/utilities';

/**
 * Reveals heading text when it enters the viewport.
 *
 * Two modes (see snippets/split-words.liquid and blocks/_heading.liquid):
 * - default: children are per-word spans rendered by Liquid, staggered via `--word-index`;
 * - `data-split-mode="lines"`: wraps an untouched rich-text block; direct children of the
 *   inner `.text-block` are staggered per line via `--line-index` (no HTML is re-cut).
 *
 * Follows the jumbo-text pattern: content is visible without JS; the `ready` class opts
 * into the hidden initial state, `split-words-visible` triggers the reveal.
 *
 * Inside a slideshow-slide (hero banner) the component switches to slide mode
 * (Heights `slideshow-words` choreography, measured live 2026-09-06): instead of a
 * one-shot IntersectionObserver reveal, it watches its slide's aria-hidden flips —
 * on hide the words rise out through a line-box mask (`split-words--exit`), on show
 * they rise back in from below (`split-words--shown`, with the longer
 * `split-words--rejoin` delay after the first reveal). Loop clones
 * ([data-loop-clone]) never animate: their slide's aria-hidden stays "true", so
 * their words stay masked and the wrap teleport lands on identical pixels.
 */
class SplitWordsComponent extends Component {
  /** @type {IntersectionObserver | undefined} */
  #observer;

  /** @type {MutationObserver | undefined} */
  #slideObserver;

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #exitTimer;

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #settleTimer;

  /** @type {boolean} */
  #slideVisible = false;

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #failsafeTimer;

  /** @type {(() => void) | undefined} */
  #failsafeCleanup;

  connectedCallback() {
    super.connectedCallback();

    if (prefersReducedMotion()) return;

    if (this.dataset.splitMode === 'lines') this.#indexLines();


    // Inside the hero word overlay (slideshow-word-overlay.js) the controller
    // owns every state class — register nothing here. The mask base state
    // comes from the split-words--slide class the controller applies.
    if (this.closest('[data-word-overlay-item]')) {
      this.classList.add('split-words--slide');
      return;
    }
    const slide = this.closest('slideshow-slide');
    if (slide instanceof HTMLElement) {
      this.#initSlideMode(slide);
      return;
    }

    this.classList.add('ready');

    this.#observer = new IntersectionObserver(this.#onIntersect, { threshold: 0.3 });
    this.#observer.observe(this);

    // Masked-forever text is the one unacceptable outcome: if the observer
    // never delivers on this engine, reveal as soon as the words are
    // measurably on screen.
    this.#armFailsafe(() => {
      if (this.classList.contains('split-words-visible')) return true;
      if (!this.#onScreen()) return false;
      this.classList.add('split-words-visible');
      this.#observer?.disconnect();
      this.#observer = undefined;
      return true;
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#observer?.disconnect();
    this.#slideObserver?.disconnect();
    clearTimeout(this.#exitTimer);
    clearTimeout(this.#settleTimer);
    this.#disarmFailsafe();
  }

  /** @returns {boolean} whether this element has size and intersects the viewport */
  #onScreen() {
    const rect = this.getBoundingClientRect();
    if (!rect.width || !rect.height) return false;
    if (rect.bottom <= 0 || rect.top >= window.innerHeight) return false;
    return rect.right > 0 && rect.left < window.innerWidth;
  }

  /**
   * Arms a reveal failsafe that re-checks on a timer and on scroll/resize
   * until `check` reports success. IntersectionObserver stays the primary
   * trigger; this only guarantees text can never stay masked forever.
   * @param {() => boolean} check - returns true once resolved (cleans up)
   */
  #armFailsafe(check) {
    this.#disarmFailsafe();

    let rafId = 0;
    const run = () => {
      rafId = 0;
      if (check()) this.#disarmFailsafe();
    };
    const onScrollOrResize = () => {
      if (!rafId) rafId = requestAnimationFrame(run);
    };

    window.addEventListener('scroll', onScrollOrResize, { passive: true });
    window.addEventListener('resize', onScrollOrResize);
    this.#failsafeTimer = setTimeout(run, 1500);
    this.#failsafeCleanup = () => {
      window.removeEventListener('scroll', onScrollOrResize);
      window.removeEventListener('resize', onScrollOrResize);
      if (rafId) cancelAnimationFrame(rafId);
      clearTimeout(this.#failsafeTimer);
    };
  }

  #disarmFailsafe() {
    this.#failsafeCleanup?.();
    this.#failsafeCleanup = undefined;
  }

  /**
   * Heights hero slide mode: word reveals follow the host slide's visibility.
   * @param {HTMLElement} slide
   */
  #initSlideMode(slide) {
    this.classList.add('split-words--slide');

    // Wrap clones are inert scenery — keep their words masked so the
    // scroll-end teleport onto the real slide swaps identical (textless)
    // pixels, then the real slide runs the rise-in.
    if (slide.hasAttribute('data-loop-clone')) {
      this.classList.remove('split-words--shown', 'split-words--rejoin', 'split-words--settled');
      return;
    }

    // Missing aria-hidden means no slideshow machinery is managing this slide
    // (single-slide banner before/without slideshow.js) — treat it as visible.
    this.#slideVisible = slide.getAttribute('aria-hidden') !== 'true';

    // Initial reveal is double-gated like Heights' Motion.inView: the slide
    // must be the selected one (aria-hidden=false) AND the words must be in
    // the viewport — mid-page banner slideshows are "visible" from page load
    // and would otherwise burn their reveal behind the fold. After a DOM move
    // (the slideshow's far-jump reorder trick reconnects the element) the
    // classes persist — don't re-run the reveal.
    if (this.#slideVisible && !this.classList.contains('split-words--shown')) {
      this.#observer = new IntersectionObserver(this.#onSlideIntersect, { threshold: 0.3 });
      this.#observer.observe(this);
    }

    // Dead-man switch: this component masks its words immediately but relies
    // on slideshow.js (aria-hidden flips) and the observer above to unmask.
    // If either never delivers — the slideshow module failing to load on a
    // flaky mobile connection blanks every hero/banner heading — reveal as
    // soon as the masked words are measurably on screen. Slides the
    // machinery is actively hiding (aria-hidden=true while slideshow.js is
    // alive) keep waiting for their flip; exiting words are never re-shown.
    this.#armFailsafe(() => {
      if (this.classList.contains('split-words--shown')) return true;
      if (this.classList.contains('split-words--exit')) return false;
      const machineryAlive = !!customElements.get('slideshow-component');
      if (machineryAlive && slide.getAttribute('aria-hidden') === 'true') return false;
      if (!this.#onScreen()) return false;
      this.#slideVisible = true;
      this.#show(false);
      return true;
    });

    this.#slideObserver = new MutationObserver(() => {
      const visible = slide.getAttribute('aria-hidden') === 'false';
      if (visible === this.#slideVisible) return;
      this.#slideVisible = visible;

      if (visible) {
        this.#show(true);
      } else {
        this.#hide();
      }
    });
    this.#slideObserver.observe(slide, { attributes: true, attributeFilter: ['aria-hidden'] });
  }

  /**
   * @param {IntersectionObserverEntry[]} entries
   */
  #onSlideIntersect = (entries) => {
    for (const entry of entries) {
      if (!entry.isIntersecting) continue;

      this.#observer?.disconnect();
      this.#observer = undefined;
      if (this.#slideVisible && !this.classList.contains('split-words--shown')) this.#show(false);
      break;
    }
  };

  /**
   * Rise the words in from below the mask.
   * @param {boolean} fromSwap - true when triggered by a slide change (waits
   *   the extra Heights word-flip beat via the --rejoin delay).
   */
  #show(fromSwap) {
    clearTimeout(this.#exitTimer);
    clearTimeout(this.#settleTimer);

    this.classList.remove('split-words--exit');
    // Land in the masked below-the-line state before transitioning up.
    void this.offsetWidth;
    this.classList.toggle('split-words--rejoin', fromSwap);
    this.classList.add('split-words--shown');

    // Heights unclips words once the rise settles (.word.animated):
    // delay (.25s/.78s) + 1s duration + worst-case stagger.
    this.#settleTimer = setTimeout(() => this.classList.add('split-words--settled'), fromSwap ? 2000 : 1500);
  }

  /** Rise the words out through the top of the mask, then re-arm below it. */
  #hide() {
    clearTimeout(this.#exitTimer);
    clearTimeout(this.#settleTimer);

    this.classList.remove('split-words--shown', 'split-words--rejoin', 'split-words--settled');
    this.classList.add('split-words--exit');

    // Once the exit finishes (delay .25s + 1s + stagger), drop back to the
    // transitionless base state: instantly re-masked below the line.
    this.#exitTimer = setTimeout(() => this.classList.remove('split-words--exit'), 1500);
  }

  #indexLines() {
    const block = this.querySelector('.text-block');
    if (!block) return;

    let index = 0;
    for (const line of block.children) {
      if (!(line instanceof HTMLElement)) continue;
      line.style.setProperty('--line-index', `${index}`);
      index += 1;
    }
  }

  /**
   * @param {IntersectionObserverEntry[]} entries
   */
  #onIntersect = (entries) => {
    for (const entry of entries) {
      if (!entry.isIntersecting) continue;

      this.classList.add('split-words-visible');
      this.#observer?.disconnect();
      this.#observer = undefined;
      break;
    }
  };
}

if (!customElements.get('split-words-component')) {
  customElements.define('split-words-component', SplitWordsComponent);
}
